import { pageSxFor } from '../components/pageLayout';
import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Circle,
  CircleMarker,
  GeoJSON,
  MapContainer,
  Polygon,
  Polyline,
  Rectangle,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import { AddLocationAltOutlined, CheckCircleOutline, DrawOutlined, EditOutlined, FlightOutlined, LayersOutlined, MapOutlined, MyLocation } from "@mui/icons-material";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import RectangleOutlinedIcon from "@mui/icons-material/RectangleOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import { Search } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { alpha } from "@mui/material/styles";
import { Chip } from "@mui/material"; // add Chip to your existing @mui/material import instead if you prefer
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { ROLES } from "../config/roles";
import { useAuthContext } from "../context/auth";
import { usePathContext } from "../context/path";
import { linkedCountries } from "../constants/linkedCountries";
import tanzaniaGeoJSON1 from "../assets/TZA-ADM1.json";
import CenterMap from "../components/map/CenterMap";
import ZoomTracker from "../components/map/ZoomTracker";
import AirportMarker from "../components/map/AirportMarker";
import OutpostMarker from "../components/map/OutpostMarker";
import GrayOverlay from "../components/map/GrayOverlay";
import LinkedBorders from "../components/map/LinkedBorders";
import FlexBetween from "../components/FlexBetween";
import Header from "../components/Header";
import PageBanner from '../components/PageBanner';
import MapSettingsSidebar from "../components/map/MapSettingsSidebar";
import CustomColumnMenu from "../components/DataGridCustomColumnMenu";
import Delete from "../components/regions/Delete";
import DeleteAirport from "../components/regions/DeleteAirport";
import {RegionLayers, regionToShape } from "../components/regions/RegionLayers";
import useGridStyles from "../hooks/useGridStyles";
import useDialogStyles from "../hooks/useDialogStyles";
import { SubmitButton } from "../components/dialogKit";

/* ------------------------------------------------------------------ */
/* Drawing helpers                                                     */
/* ------------------------------------------------------------------ */

const TYPES = [
  { value: "Polygon", Icon: PentagonOutlinedIcon },
  { value: "Rectangle", Icon: RectangleOutlinedIcon },
  { value: "Circle", Icon: CircleOutlinedIcon },
];

const HINTS = {
  Polygon: "Click the map to add corners (at least 3).",
  Rectangle: "Click two opposite corners. A third click starts over.",
  Circle: "Click the center, then a point on the edge. A third click starts over.",
};

const MIN_POINTS = { Polygon: 3, Rectangle: 2, Circle: 2 };
const MAX_POINTS = { Polygon: Infinity, Rectangle: 2, Circle: 2 };

const DRAW_COLOR = "#d05858";

const AIRPORT_TYPES = [
  { value: "small_airport", label: "Small" },
  { value: "medium_airport", label: "Medium" },
  { value: "large_airport", label: "Large" },
  { value: "heliport", label: "Heliport" },
];
const airportTypeLabel = (value) => AIRPORT_TYPES.find((t) => t.value === value)?.label || value;

// Two opposite corners -> four corners (SW, NW, NE, SE)
const rectangleCorners = ([a, b]) => {
  const south = Math.min(a.lat, b.lat);
  const north = Math.max(a.lat, b.lat);
  const west = Math.min(a.long, b.long);
  const east = Math.max(a.long, b.long);
  return [
    { lat: south, long: west },
    { lat: north, long: west },
    { lat: north, long: east },
    { lat: south, long: east },
  ];
};

const toPayloadPoints = (type, points) =>
  type === "Rectangle" ? rectangleCorners(points) : points;

// Saved region -> the raw clicks the drawing tools work with (a rectangle is stored as 4 corners)
const regionToDraftPoints = (region) => {
  const pts = (region.points || []).map((p) => ({ lat: p.lat, long: p.long }));
  return region.type === "Rectangle" && pts.length === 4 ? [pts[0], pts[2]] : pts;
};

const shapeBounds = (shape) =>
  shape.kind === "circle"
    ? L.latLng(shape.center).toBounds(shape.radius * 2) // toBounds takes the side length in meters
    : L.latLngBounds(shape.positions);

const ClickHandler = ({ onClick }) => {
  useMapEvents({
    click: (e) => onClick({ lat: e.latlng.lat, long: e.latlng.lng }),
  });
  return null;
};

// Zooms the map to the region selected in the grid
const FitToRegion = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }, [bounds, map]);
  return null;
};

const DrawingPreview = ({ type, points }) => {
  const latlngs = points.map((p) => [p.lat, p.long]);
  const pathOptions = { color: DRAW_COLOR, fillColor: DRAW_COLOR, fillOpacity: 0.3, weight: 2 };

  return (
    <>
      {latlngs.map((ll, i) => (
        <CircleMarker
          key={i}
          center={ll}
          radius={4}
          pathOptions={{ color: "#ffffff", fillColor: DRAW_COLOR, fillOpacity: 1, weight: 2 }}
        />
      ))}

      {type === "Polygon" && latlngs.length === 2 && (
        <Polyline positions={latlngs} pathOptions={pathOptions} />
      )}
      {type === "Polygon" && latlngs.length >= 3 && (
        <Polygon positions={latlngs} pathOptions={pathOptions} />
      )}
      {type === "Rectangle" && latlngs.length === 2 && (
        <Rectangle bounds={latlngs} pathOptions={pathOptions} />
      )}
      {type === "Circle" && latlngs.length === 2 && (
        <Circle
          center={latlngs[0]}
          radius={L.latLng(latlngs[0]).distanceTo(L.latLng(latlngs[1]))}
          pathOptions={pathOptions}
        />
      )}
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const Region = () => {
  const theme = useTheme();
  const { gridSx, gridBorder, selectedRowSx } = useGridStyles();
  const { fieldSx, cancelButtonSx, menuProps } = useDialogStyles();
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.secondary.main;

  const { auth } = useAuthContext();
  const { setTitle } = usePathContext();
  const axiosPrivate = useAxiosPrivate();

  const mapRef = useRef(null);
  const [scale, setScale] = useState(Math.pow(6 / 6, 1.2));
  const [zoom] = useState(6);
  const [centerPosition] = useState([-6.369028, 34.888822]);

  const isRoot = auth.roles.includes(ROLES.Root);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBorder, setShowBorder] = useState(false);
  const [showGoogleMap, setShowGoogleMap] = useState(true);
  const [showAirports, setShowAirports] = useState(false);
  const [showOutposts, setShowOutposts] = useState(true);
  const [overlayOpacity, setOverlayOpacity] = useState(0.7);
  const [airportOpacity, setAirportOpacity] = useState(0.3);
  const [outpostOpacity, setOutpostOpacity] = useState(0.5);

  // These still use the old "NoFlyRegion" names so MapSettingsSidebar works unchanged.
  // They now control the saved-regions layer.
  const [showRegions, setShowRegions] = useState(true);
  const [regionColor, setRegionColor] = useState("115, 31, 31");
  const [regionFillColor, setRegionFillColor] = useState("114, 74, 74");

  const [query, setQuery] = useState("");
  const isNonMediumScreen = useMediaQuery("(min-width: 1200px)");
  const [mapSettingsSidebarOpen, setMapSettingsSidebarOpen] = useState(false);

  // Regions list
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [regionError, setRegionError] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  // Drawing (the former Add dialog)
  const [drawing, setDrawing] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("Polygon");
  const [points, setPoints] = useState([]); // raw clicks: [{ lat, long }]
  const [saving, setSaving] = useState(false);

  // Left panel tab: "regions" | "airports"
  const [tab, setTab] = useState("regions");

  // Airports added by hand (stored in the database; the CSV list is separate)
  const [airports, setAirports] = useState([]);
  const [loadingAirports, setLoadingAirports] = useState(false);
  const [airportError, setAirportError] = useState("");
  const [selectedAirportId, setSelectedAirportId] = useState(null);

  // Airport draft: filled in on the toolbar, position picked by clicking the map
  const [apName, setApName] = useState("");
  const [apCode, setApCode] = useState("");
  const [apType, setApType] = useState("small_airport");
  const [apPoint, setApPoint] = useState(null); // { lat, lng }
  const [apSaving, setApSaving] = useState(false);

  const canSubmitAirport = apName.trim().length > 0 && !!apPoint;

  const pointsReady = points.length >= MIN_POINTS[type];
  const canSubmit = name.trim().length > 0 && pointsReady;

  /* ---------------- data loading ---------------- */

  useEffect(() => {
    setTitle("Region Management");

    let isMounted = true;

    const loadRegions = async () => {
      setLoadingRegions(true);
      setRegionError("");

      try {
        const response = await axiosPrivate.get("/api/regions");
        if (isMounted) {
          setRegions(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        console.error("Error fetching regions:", err);
        if (isMounted) {
          setRegionError(err.response?.data?.error || "Failed to load regions.");
        }
      } finally {
        if (isMounted) setLoadingRegions(false);
      }
    };

    loadRegions();

    return () => {
      isMounted = false;
    };
  }, [setTitle, axiosPrivate]);

  useEffect(() => {
    let isMounted = true;

    const loadAirports = async () => {
      setLoadingAirports(true);
      setAirportError("");

      try {
        const response = await axiosPrivate.get("/api/airports");
        if (isMounted) setAirports(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching airports:", err);
        if (isMounted) setAirportError(err.response?.data?.error || "Failed to load airports.");
      } finally {
        if (isMounted) setLoadingAirports(false);
      }
    };

    loadAirports();

    return () => {
      isMounted = false;
    };
  }, [axiosPrivate]);

  // Detect online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowBorder(false);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setShowBorder(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const filteredRegions = useMemo(
    () => regions.filter((r) => r.name?.toLowerCase().includes(query.toLowerCase())),
    [regions, query]
  );

  const filteredAirports = useMemo(() => {
    const q = query.toLowerCase();
    return airports.filter(
      (a) => a.name?.toLowerCase().includes(q) || a.code?.toLowerCase().includes(q)
    );
  }, [airports, query]);

  const selectedRegion = regions.find((r) => r._id === selectedId) || null;
  const selectedAirport = airports.find((a) => a._id === selectedAirportId) || null;

  // Selecting an item (Root) puts the right panel into edit mode for it
  const editingRegion = isRoot && tab === "regions" ? selectedRegion : null;
  const editingAirport = isRoot && tab === "airports" ? selectedAirport : null;

  // The map zooms to whatever is selected in the active tab
  const selectedBounds = useMemo(() => {
    if (tab === "airports") {
      const ap = airports.find((a) => a._id === selectedAirportId);
      return ap ? L.latLng(ap.lat, ap.lng).toBounds(20000) : null; // ~20 km square around the airport
    }
    const region = regions.find((r) => r._id === selectedId);
    const shape = region && regionToShape(region);
    return shape ? shapeBounds(shape) : null;
  }, [tab, regions, selectedId, airports, selectedAirportId]);

  /* ---------------- region handlers ---------------- */

  const handleActiveChange = async (id, active) => {
    const updateRegion = (changes) =>
      setRegions((prev) => prev.map((r) => (r._id === id ? { ...r, ...changes } : r)));

    updateRegion({ active }); // optimistic

    try {
      const response = await axiosPrivate.patch(`/api/regions/${id}/active`, { active });
      if (response.data?._id) updateRegion(response.data);
    } catch (err) {
      console.error("Error updating region status:", err);
      updateRegion({ active: !active });
      setRegionError(err.response?.data?.error || "Failed to update region status.");
    }
  };

  const handleRegionDeleted = (id) => {
    setRegions((prev) => prev.filter((r) => r._id !== id));
    if (id === selectedId) cancelRegionEdit();
  };

  const handleRegionAdded = (region) => {
    if (!region?._id) return;
    setRegions((prev) => [region, ...prev.filter((r) => r._id !== region._id)]);
  };

  // Row click: select (zoom to it) and, for Root, load it into the form to edit
  const selectRegion = (region) => {
    setSelectedId(region._id);
    if (!isRoot) return;
    setName(region.name || "");
    setType(region.type);
    setPoints(regionToDraftPoints(region));
  };

  const selectAirport = (airport) => {
    setSelectedAirportId(airport._id);
    if (!isRoot) return;
    setApName(airport.name || "");
    setApCode(airport.code || "");
    setApType(airport.type || "small_airport");
    setApPoint({ lat: airport.lat, lng: airport.lng });
  };

  // Leave edit mode and empty the form, ready to add a new one
  const cancelRegionEdit = () => {
    setSelectedId(null);
    setPoints([]);
    setName("");
  };

  const cancelAirportEdit = () => {
    setSelectedAirportId(null);
    setApName("");
    setApCode("");
    setApPoint(null);
  };

  const handleAirportDeleted = (id) => {
    setAirports((prev) => prev.filter((a) => a._id !== id));
    if (id === selectedAirportId) cancelAirportEdit();
  };

  const handleTabChange = (_, next) => {
    setTab(next);
    if (next === "airports") setShowAirports(true); // so the airports are visible on the map
  };

  /* ---------------- drawing handlers ---------------- */

  const handleTypeChange = (_, next) => {
    if (!next || next === type) return;
    setType(next);
    setPoints([]); // point meaning differs per type
  };

  const resetDraft = () => {
    setPoints([]);
    setName("");
  };

  const resetAirportDraft = () => {
    setApName("");
    setApCode("");
    setApPoint(null);
  };

  const handleSaveAirport = async () => {
    if (!canSubmitAirport) return;

    setAirportError("");
    setApSaving(true);
    const body = {
      name: apName.trim(),
      code: apCode.trim(),
      type: apType,
      lat: apPoint.lat,
      lng: apPoint.lng,
    };

    try {
      if (editingAirport) {
        const res = await axiosPrivate.patch(`/api/airports/${editingAirport._id}`, body);
        setAirports((prev) => prev.map((a) => (a._id === res.data._id ? res.data : a)));
        cancelAirportEdit(); // done: back to add mode
        return;
      }

      const res = await axiosPrivate.post("/api/airports", body);
      setAirports((prev) => [res.data, ...prev.filter((a) => a._id !== res.data._id)]);
      resetAirportDraft(); // keep the chosen airport type for the next one
    } catch (err) {
      setAirportError(err.response?.data?.error || (editingAirport ? "Failed to update airport" : "Failed to add airport"));
    } finally {
      setApSaving(false);
    }
  };

  const handleMapClick = (point) => {
    if (!isRoot) return;
    if (tab === "airports") {
      setApPoint({ lat: point.lat, lng: point.long });
      return;
    }
    setPoints((prev) => (prev.length >= MAX_POINTS[type] ? [point] : [...prev, point]));
  };

  const handleSave = async () => {
    if (!canSubmit) return;

    setRegionError("");
    setSaving(true);
    try {
      if (editingRegion) {
        const res = await axiosPrivate.patch("/api/regions", {
          id: editingRegion._id,
          name: name.trim(),
          type,
          points: toPayloadPoints(type, points),
        });
        setRegions((prev) => prev.map((r) => (r._id === res.data._id ? res.data : r)));
        cancelRegionEdit(); // done: back to add mode
        return;
      }

      const res = await axiosPrivate.post("/api/regions", {
        name: name.trim(),
        type,
        points: toPayloadPoints(type, points),
        active: true,
        enabled: true, // remove whichever field your schema doesn't use
      });
      handleRegionAdded(res.data);
      resetDraft(); // keep the selected shape type for the next region
    } catch (err) {
      setRegionError(err.response?.data?.error || (editingRegion ? "Failed to update region" : "Failed to add region"));
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- grid columns ---------------- */

  const allColumns = [
    {
      field: "order",
      headerName: "No.",
      flex: 0.1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params.api || !params.id) return null;
        const index = params.api.getAllRowIds?.().indexOf(params.id);
        return <span>{index + 1}</span>;
      },
    },
    { field: "name", headerName: "Name", flex: 0.5 },
    { field: "type", headerName: "Type", flex: 0.3 },
    {
      field: "active",
      headerName: "Status",
      flex: 0.2,
      renderCell: (params) => (
        <Switch
          checked={!!params.row.active}
          disabled={!isRoot}
          onClick={(e) => e.stopPropagation()} // don't select the row
          onChange={(event) => handleActiveChange(params.row._id, event.target.checked)}
          sx={{
            width: 42,
            height: 24,
            padding: 0,
            "& .MuiSwitch-switchBase": {
              padding: "3px",
              "&.Mui-checked": {
                transform: "translateX(18px)",
                color: theme.palette.getContrastText(theme.palette.secondary.main),
                "& + .MuiSwitch-track": {
                  backgroundColor: theme.palette.secondary.main,
                  opacity: 1,
                },
              },
            },
            "& .MuiSwitch-thumb": {
              width: 18,
              height: 18,
              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
            },
            "& .MuiSwitch-track": {
              borderRadius: 12,
              backgroundColor: theme.palette.grey[600],
              opacity: 1,
            },
          }}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      flex: 0.2,
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Delete
            id={params.row._id}
            name={params.row.name}
            onDeleted={handleRegionDeleted}
          />
        </div>
      ),
    },
  ];

  const columnsRegions = isRoot
    ? allColumns
    : allColumns.filter((col) => col.field !== "actions");

  const airportColumns = [
    {
      field: "order",
      headerName: "No.",
      flex: 0.1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params.api || !params.id) return null;
        const index = params.api.getAllRowIds?.().indexOf(params.id);
        return <span>{index + 1}</span>;
      },
    },
    { field: "name", headerName: "Name", flex: 0.5 },
    { field: "code", headerName: "Code", flex: 0.2 },
    {
      field: "type",
      headerName: "Type",
      flex: 0.25,
      renderCell: (params) => airportTypeLabel(params.row.type),
    },
    ...(isRoot
      ? [
          {
            field: "actions",
            type: "actions",
            headerName: "Action",
            flex: 0.2,
            renderCell: (params) => (
              <div onClick={(e) => e.stopPropagation()}>
                <DeleteAirport
                  id={params.row._id}
                  name={params.row.name}
                  onDeleted={handleAirportDeleted}
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  const toolButtonSx = {
    ...cancelButtonSx,
    textTransform: "none",
    "&.Mui-disabled": { color: theme.palette.text.disabled },
  };

  /* ---------------- render ---------------- */

  return (
    <>
      <Box sx={pageSxFor(isNonMediumScreen)}>
        <FlexBetween
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: "1rem",
          }}
        >
          <Header
            title="Regions"
            subtitle={
              isRoot
                ? "List Regions and Airports, Add or Delete them"
                : "List Regions and Airports"
            }
          />
          <PageBanner />

          <FlexBetween>
            <TextField
              label={tab === "airports" ? "Search by Airport Name or Code" : "Search by Region Name"}
              placeholder="Type to search..."
              variant="standard"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ mb: "0.5rem", mr: "1rem", width: tab === "airports" ? "19rem" : "15rem" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search sx={{ color: theme.palette.secondary[200] }} />
                  </InputAdornment>
                ),
              }}
            />
          </FlexBetween>
        </FlexBetween>

        <Box
          mt="1rem"
          display="grid"
          gridTemplateColumns={"repeat(8, 1fr)"}
          gap="20px"
          sx={{
            // Wide screens: one row that fills the space under the header; narrow: cards stack
            flex: isNonMediumScreen ? 1 : "none",
            minHeight: isNonMediumScreen ? 320 : 0,
            gridTemplateRows: isNonMediumScreen ? "minmax(0, 1fr)" : "none",
            gridAutoRows: isNonMediumScreen ? undefined : "70vh",
            "& > div": {
              minHeight: 0,
              gridColumn: isNonMediumScreen ? undefined : "span 12",
            },
          }}
        >
          {/* ---------- Left: grid panel ---------- */}
          <Box
            gridColumn="span 3"
            gridRow="span 1"
            display="flex"
            flexDirection="column"
            backgroundColor={theme.palette.background.alt}
            border={gridBorder}
            borderRadius="0.55rem"
            sx={{ padding: "0px", minHeight: 0 }}
            overflow="hidden"
          >
            {/* Tabs: one list per kind, each with its count */}
            <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                variant="fullWidth"
                aria-label="Regions and airports"
                sx={{
                  minHeight: 40,
                  p: 0.5,
                  borderRadius: 2,
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.18)" : theme.palette.divider}`,
                  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : theme.palette.background.default,
                  "& .MuiTabs-indicator": { display: "none" },
                  "& .MuiTab-root": {
                    minHeight: 32,
                    py: 0,
                    gap: 0.75,
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    color: theme.palette.text.secondary,
                    "&:hover": { backgroundColor: alpha(accent, 0.08) },
                    "&.Mui-selected": {
                      color: accent,
                      backgroundColor: alpha(accent, 0.14),
                      boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.5)}`,
                    },
                  },
                }}
              >
                {[
                  { value: "regions", label: "Regions", Icon: MapOutlined, count: regions.length },
                  { value: "airports", label: "Airports", Icon: FlightOutlined, count: airports.length },
                ].map(({ value, label, Icon, count }) => (
                  <Tab
                    key={value}
                    value={value}
                    iconPosition="start"
                    icon={<Icon sx={{ fontSize: 18 }} />}
                    label={
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                        {label}
                        <Box
                          component="span"
                          sx={{
                            px: 0.75,
                            minWidth: 22,
                            borderRadius: 10,
                            fontSize: "0.7rem",
                            lineHeight: "18px",
                            textAlign: "center",
                            backgroundColor: alpha(accent, tab === value ? 0.22 : 0.1),
                          }}
                        >
                          {count}
                        </Box>
                      </Box>
                    }
                  />
                ))}
              </Tabs>

              <Typography variant="caption" sx={{ display: "block", mt: 0.75, px: 0.5, color: theme.palette.text.secondary }}>
                {tab === "airports"
                  ? `${filteredAirports.length} shown${query ? ` for "${query}"` : ""} · on top of the built-in airport list`
                  : `${filteredRegions.length} shown${query ? ` for "${query}"` : ""} · ${regions.filter((r) => r.active).length} active`}
              </Typography>
            </Box>

            {tab === "regions" && regionError && (
              <Alert severity="error" onClose={() => setRegionError("")} sx={{ mx: 1, mb: 1 }}>
                {regionError}
              </Alert>
            )}
            {tab === "airports" && airportError && (
              <Alert severity="error" onClose={() => setAirportError("")} sx={{ mx: 1, mb: 1 }}>
                {airportError}
              </Alert>
            )}

            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
              {tab === "regions" ? (
                <DataGrid
                  loading={loadingRegions}
                  getRowId={(row) => row._id}
                  rows={filteredRegions}
                  columns={columnsRegions}
                  disableRowSelectionOnClick
                  onRowClick={(params) => !drawing && selectRegion(params.row)}
                  getRowClassName={(params) =>
                    params.row._id === selectedId ? "region-selected" : ""
                  }
                  components={{ ColumnMenu: CustomColumnMenu }}
                  sx={{
                    ...gridSx,
                    "& .MuiDataGrid-row": { cursor: "pointer" },
                    "& .region-selected": selectedRowSx,
                  }}
                />
              ) : (
                <DataGrid
                  loading={loadingAirports}
                  getRowId={(row) => row._id}
                  rows={filteredAirports}
                  columns={airportColumns}
                  disableRowSelectionOnClick
                  onRowClick={(params) => selectAirport(params.row)}
                  getRowClassName={(params) =>
                    params.row._id === selectedAirportId ? "region-selected" : ""
                  }
                  components={{ ColumnMenu: CustomColumnMenu }}
                  sx={{
                    ...gridSx,
                    "& .MuiDataGrid-row": { cursor: "pointer" },
                    "& .region-selected": selectedRowSx,
                  }}
                />
              )}
            </Box>
          </Box>

          {/* ---------- Right: header + drawing toolbar + map ---------- */}
          <Box
            gridColumn="span 5"
            gridRow="span 1"
            display="flex"
            flexDirection="column"
            backgroundColor={theme.palette.background.alt}
            border={gridBorder}
            borderRadius="0.55rem"
            sx={{ padding: "0px", minHeight: 0 }}
            overflow="hidden"
          >
            {/* Header: what the panel is + the region shown on the map */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.25,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  flexShrink: 0,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  color: accent,
                  backgroundColor: alpha(accent, 0.12),
                  border: `1px solid ${alpha(accent, 0.4)}`,
                }}
              >
                {tab === "airports"
                  ? editingAirport ? <EditOutlined sx={{ fontSize: 18 }} /> : <AddLocationAltOutlined sx={{ fontSize: 18 }} />
                  : editingRegion ? <EditOutlined sx={{ fontSize: 18 }} />
                  : isRoot ? <DrawOutlined sx={{ fontSize: 18 }} /> : <MapOutlined sx={{ fontSize: 18 }} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.98rem", lineHeight: 1.2 }}>
                  {tab === "airports"
                    ? editingAirport ? "Edit airport" : isRoot ? "Add an airport" : "Airport map"
                    : editingRegion ? "Edit region" : isRoot ? "Draw a new region" : "Region map"}
                </Typography>
                <Typography noWrap variant="caption" sx={{ display: "block", color: theme.palette.text.secondary }}>
                  {tab === "airports"
                    ? editingAirport ? "Change the details or click the map to move it, then save"
                    : isRoot ? "Name it, pick a type, then click the map to place it" : "Click an airport in the list to zoom to it"
                    : editingRegion ? "Change the name or shape, redraw on the map if needed, then save"
                    : isRoot ? "Name it, pick a shape, then click the map" : "Click a region in the list to zoom to it"}
                </Typography>
              </Box>

              {(tab === "airports" ? selectedAirport : selectedRegion) && (
                <Chip
                  size="small"
                  icon={<MyLocation />}
                  label={(tab === "airports" ? selectedAirport : selectedRegion).name}
                  onDelete={() => (tab === "airports" ? cancelAirportEdit() : cancelRegionEdit())}
                  sx={{
                    maxWidth: 220,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    backgroundColor: alpha(accent, 0.14),
                    border: `1px solid ${alpha(accent, 0.35)}`,
                    "& .MuiChip-icon": { color: accent, fontSize: 16 },
                    "& .MuiChip-deleteIcon": { color: theme.palette.text.secondary, "&:hover": { color: theme.palette.text.primary } },
                  }}
                />
              )}
            </Box>

            {/* Drawing toolbar (Root only) */}
            {isRoot && tab === "regions" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                }}
              >
                {/* Row 1: name + shape */}
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                  <TextField
                    label="Region name"
                    placeholder="e.g. Dodoma no-fly zone"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    size="small"
                    disabled={saving}
                    autoComplete="off"
                    sx={{ ...fieldSx, flex: "1 1 220px", minWidth: 0 }}
                  />

                  <ToggleButtonGroup
                    value={type}
                    exclusive
                    size="small"
                    onChange={handleTypeChange}
                    disabled={saving}
                    aria-label="Shape"
                    sx={{
                      height: 40,
                      p: 0.375,
                      gap: 0.5,
                      borderRadius: 1.5,
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.28)" : theme.palette.neutral[300]}`,
                      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : theme.palette.background.default,
                      "& .MuiToggleButton-root": {
                        gap: 0.75,
                        px: 1.25,
                        border: "none",
                        borderRadius: "6px !important",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        color: theme.palette.text.secondary,
                        "&:hover": { backgroundColor: alpha(accent, 0.08) },
                        "&.Mui-selected, &.Mui-selected:hover": {
                          color: accent,
                          backgroundColor: alpha(accent, 0.14),
                          boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.5)}`,
                        },
                      },
                    }}
                  >
                    {TYPES.map(({ value, Icon }) => (
                      <ToggleButton key={value} value={value}>
                        <Icon sx={{ fontSize: 18 }} />
                        {value}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>

                {/* Row 2: hint + progress (left), actions (right) */}
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      flex: "1 1 280px",
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: `1px dashed ${pointsReady ? alpha(theme.palette.success.main, 0.5) : alpha(accent, 0.35)}`,
                      backgroundColor: pointsReady ? alpha(theme.palette.success.main, 0.08) : alpha(accent, 0.05),
                    }}
                  >
                    {pointsReady ? (
                      <CheckCircleOutline sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    ) : (
                      <InfoOutlinedIcon sx={{ fontSize: 18, color: accent }} />
                    )}
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: "0.8rem", color: theme.palette.text.secondary }}>
                      {pointsReady && !name.trim() ? "Shape ready. Give it a name to save." : HINTS[type]}
                    </Typography>
                    <Typography
                      sx={{
                        flexShrink: 0,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        fontFamily: "'Roboto Mono', Consolas, monospace",
                        color: pointsReady ? theme.palette.success.main : theme.palette.text.secondary,
                      }}
                    >
                      {points.length}/{MIN_POINTS[type]}{type === "Polygon" ? "+" : ""} pts
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
                    <Button
                      size="small"
                      startIcon={<UndoIcon />}
                      sx={toolButtonSx}
                      onClick={() => setPoints((prev) => prev.slice(0, -1))}
                      disabled={points.length === 0 || saving}
                    >
                      Undo
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ClearAllIcon />}
                      sx={toolButtonSx}
                      onClick={editingRegion ? () => setPoints([]) : resetDraft}
                      disabled={(points.length === 0 && (editingRegion || !name)) || saving}
                    >
                      {editingRegion ? "Redraw" : "Clear"}
                    </Button>
                    {editingRegion && (
                      <Button size="small" sx={toolButtonSx} onClick={cancelRegionEdit} disabled={saving}>
                        Cancel
                      </Button>
                    )}
                    <SubmitButton
                      label={editingRegion ? "Save changes" : "Save region"}
                      busyLabel="Saving…"
                      busy={saving}
                      disabled={!canSubmit}
                      onClick={handleSave}
                      icon={<SaveOutlinedIcon />}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Airport toolbar (Root only) */}
            {isRoot && tab === "airports" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                }}
              >
                {/* Row 1: name + code + type */}
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                  <TextField
                    label="Airport name"
                    placeholder="e.g. Dodoma Airport"
                    value={apName}
                    onChange={(e) => setApName(e.target.value)}
                    size="small"
                    disabled={apSaving}
                    autoComplete="off"
                    sx={{ ...fieldSx, flex: "1 1 200px", minWidth: 0 }}
                  />
                  <TextField
                    label="Code"
                    placeholder="DOD"
                    value={apCode}
                    onChange={(e) => setApCode(e.target.value.toUpperCase())}
                    size="small"
                    disabled={apSaving}
                    autoComplete="off"
                    inputProps={{ maxLength: 8 }}
                    sx={{ ...fieldSx, width: 100 }}
                  />
                  <FormControl size="small" disabled={apSaving} sx={{ ...fieldSx, width: 130 }}>
                    <InputLabel>Type</InputLabel>
                    <Select label="Type" value={apType} onChange={(e) => setApType(e.target.value)} MenuProps={menuProps}>
                      {AIRPORT_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Row 2: position hint (left), actions (right) */}
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      flex: "1 1 280px",
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: `1px dashed ${apPoint ? alpha(theme.palette.success.main, 0.5) : alpha(accent, 0.35)}`,
                      backgroundColor: apPoint ? alpha(theme.palette.success.main, 0.08) : alpha(accent, 0.05),
                    }}
                  >
                    {apPoint ? (
                      <CheckCircleOutline sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    ) : (
                      <InfoOutlinedIcon sx={{ fontSize: 18, color: accent }} />
                    )}
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: "0.8rem", color: theme.palette.text.secondary }}>
                      {apPoint
                        ? apName.trim() ? "Position set. Click the map again to move it." : "Position set. Give it a name to save."
                        : "Click the map where the airport is."}
                    </Typography>
                    {apPoint && (
                      <Typography
                        sx={{
                          flexShrink: 0,
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          fontFamily: "'Roboto Mono', Consolas, monospace",
                          color: theme.palette.success.main,
                        }}
                      >
                        {apPoint.lat.toFixed(4)}, {apPoint.lng.toFixed(4)}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
                    {editingAirport ? (
                      <Button size="small" sx={toolButtonSx} onClick={cancelAirportEdit} disabled={apSaving}>
                        Cancel
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<ClearAllIcon />}
                        sx={toolButtonSx}
                        onClick={resetAirportDraft}
                        disabled={(!apPoint && !apName && !apCode) || apSaving}
                      >
                        Clear
                      </Button>
                    )}
                    <SubmitButton
                      label={editingAirport ? "Save changes" : "Save airport"}
                      busyLabel="Saving…"
                      busy={apSaving}
                      disabled={!canSubmitAirport}
                      onClick={handleSaveAirport}
                      icon={<SaveOutlinedIcon />}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Map area: relative wrapper so overlays stay inside the map */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                position: "relative",
                "& .leaflet-container, & .leaflet-interactive": {
                  cursor: isRoot ? "crosshair" : undefined,
                },
              }}
            >
              {!isOnline && (
                <Alert
                  severity="error"
                  sx={{ position: "absolute", bottom: 32, right: 16, zIndex: 2000, minWidth: "300px" }}
                >
                  ⚠️ Internet connection lost. Maps and data may not update.
                </Alert>
              )}

              <MapContainer
                center={centerPosition}
                zoom={zoom + 0.5}
                zoomSnap={0}
                zoomDelta={0.25}
                doubleClickZoom={!isRoot} // double-clicks would add two points and zoom
                style={{ height: "100%", width: "100%" }}
                whenCreated={(mapInstance) => {
                  mapRef.current = mapInstance;
                }}
              >
                <CenterMap center={centerPosition} zoom={zoom + 0.5} />  {/* moves and zooms together; zoom offset by 0.5 */}
                <ZoomTracker setScale={setScale} />
                <FitToRegion bounds={selectedBounds} />

                {showGoogleMap ? (
                  <TileLayer
                    key="google"
                    attribution='&copy; <a href="https://www.google.com/maps">Google Maps</a>'
                    url={`https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}&key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}`}
                  />
                ) : (
                  <TileLayer
                    key="osm"
                    attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                )}                

                {showAirports && (
                  <AirportMarker
                    center={{ lat: centerPosition[0], lng: centerPosition[1] }}
                    scale={scale}
                    opacity={airportOpacity}
                    customAirports={airports}
                  />
                )}

                {showOutposts && <OutpostMarker opacity={outpostOpacity} />}

                <GrayOverlay overlayOpacity={overlayOpacity} />

                {showBorder && (
                  <>
                    <LinkedBorders countries={linkedCountries} />
                    <GeoJSON
                      data={tanzaniaGeoJSON1}
                      style={{ color: "#FFD700", weight: 0.3, fillOpacity: 0.05, fillColor: "#A2E4D7" }}
                    />
                  </>
                )}

                {showRegions && (
                  <RegionLayers
                    regions={editingRegion ? regions.filter((r) => r._id !== editingRegion._id) : regions}
                    selectedId={selectedId}
                    color={`rgb(${regionColor})`}
                    fillColor={`rgb(${regionFillColor})`}
                  />
                )}

                {isRoot && (
                  <>
                    <ClickHandler onClick={handleMapClick} />
                    {tab === "regions" && <DrawingPreview type={type} points={points} />}
                    {tab === "airports" && apPoint && (
                      <CircleMarker
                        center={[apPoint.lat, apPoint.lng]}
                        radius={7}
                        pathOptions={{ color: "#ffffff", fillColor: DRAW_COLOR, fillOpacity: 1, weight: 2 }}
                      />
                    )}
                  </>
                )}

                {tab === "airports" && selectedAirport && (
                  <CircleMarker
                    center={[selectedAirport.lat, selectedAirport.lng]}
                    radius={14}
                    pathOptions={{ color: accent, fillColor: accent, fillOpacity: 0.15, weight: 2 }}
                  />
                )}
              </MapContainer>

              <IconButton
                aria-label="Map layers"
                title="Map layers"
                onClick={() => setMapSettingsSidebarOpen((o) => !o)}
                sx={{
                  position: "absolute",
                  top: "1.0rem",
                  right: mapSettingsSidebarOpen ? "265px" : "1rem",
                  zIndex: 1000,
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.alt, 0.92),
                  backdropFilter: "blur(6px)",
                  color: mapSettingsSidebarOpen ? accent : theme.palette.text.primary,
                  border: `1px solid ${mapSettingsSidebarOpen ? alpha(accent, 0.6) : theme.palette.divider}`,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
                  transition: "right 0.3s ease-in-out, color .15s ease, border-color .15s ease",
                  "&:hover": { color: accent, borderColor: alpha(accent, 0.6), backgroundColor: theme.palette.background.alt },
                }}
              >
                <LayersOutlined />
              </IconButton>

              {mapSettingsSidebarOpen && (
                <MapSettingsSidebar
                  showGoogleMap={showGoogleMap}
                  setShowGoogleMap={setShowGoogleMap}
                  showBorder={showBorder}
                  setShowBorder={setShowBorder}
                  showAirports={showAirports}
                  setShowAirports={setShowAirports}
                  showOutposts={showOutposts}
                  setShowOutposts={setShowOutposts}
                  outpostOpacity={outpostOpacity}
                  setOutpostOpacity={setOutpostOpacity}
                  overlayOpacity={overlayOpacity}
                  setOverlayOpacity={setOverlayOpacity}
                  showNoFlyRegion={showRegions}
                  setShowNoFlyRegion={setShowRegions}
                  noFlyRegionColor={regionColor}
                  setNoFlyRegionColor={setRegionColor}
                  noFlyRegionFillColor={regionFillColor}
                  setNoFlyRegionFillColor={setRegionFillColor}
                  airportOpacity={airportOpacity}
                  setAirportOpacity={setAirportOpacity}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Region;